import { styled, TextAreaInput } from "@monolith-forensics/monolith-ui";
import React from "react";
import { File } from "../../files.types";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { useDebouncedCallback } from "use-debounce";
import { CaseFileDefinitions } from "../../Definitions";
import { SharedWithComponent } from "./SharedWithComponent";
import { useMutation } from "@tanstack/react-query";
import { FilesAPI } from "../../files.api";
import { Link } from "react-router-dom";
import { useCaseFiles } from "../../Hooks/useCaseFiles";
import { useEvidenceItemSummary } from "@/Features/EvidenceFiles/evidence-files.query";
import {
  evidenceItemFilesPath,
  isEvidenceFile,
  isManagedFolder,
} from "@/Features/EvidenceFiles/evidence-files.utils";

const DetailItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  margin-right: 8px;
  min-width: 125px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled.div`
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
`;

// Reads as a link, like the banner's links.
const EvidenceItemLink = styled(Link)`
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;

  h3 {
    margin: 0px;
    margin-bottom: 10px;
  }

  .event-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

export const MainDetails: React.FC<{
  defaultData: File;
  onChange: () => void;
}> = ({ defaultData, onChange }) => {
  const { currentUser } = usePermissions();

  const caseFileMutation = useMutation({
    mutationFn: FilesAPI.update,
    onMutate: (newData) => {
      return newData;
    },
    onSuccess: () => {
      onChange?.();
      LoggingAPI.createLog({
        case_id: defaultData.case?.case_id,
        user_id: currentUser.user_id,
        message: `Updated ${
          defaultData.is_folder ? "folder" : "file"
        } Description`,
        event: defaultData.is_folder
          ? "case_folder:update"
          : "case_file:update",
        object_type: defaultData.is_folder ? "folder" : "file",
        object_uuid: defaultData.uuid,
      });
    },
  });

  const handleDescriptionChange = useDebouncedCallback((value) => {
    caseFileMutation.mutate({
      uuid: defaultData.uuid,
      description: value,
    });
  }, 300);

  const isFolder = defaultData.is_folder;

  // A file in an evidence item's folder links back to its item (ENG-2133),
  // except on the item's own Files tab. No row until the item lookup is back.
  const { surface } = useCaseFiles();
  // Asset attachments (ENG-2291): no case to share from, and immutable after
  // upload, so no Shared With and a read-only description.
  const caseless = surface === "asset-attachments";
  const managedFolder = isManagedFolder(defaultData);
  const readOnly = managedFolder || caseless;
  const evidenceFile =
    isEvidenceFile(defaultData) && surface !== "evidence-files-tab";
  const { data: evidenceItem } = useEvidenceItemSummary(defaultData.source_id, {
    enabled: evidenceFile,
  });

  return (
    <StyledContainer>
      <h3>
        {caseless
          ? "Attachment Details"
          : isFolder
          ? "Case Folder Details"
          : "Case File Details"}
      </h3>
      <div className="event-details">
        {CaseFileDefinitions.filter(
          (col) => col.dataField !== "description"
        ).map((col) => {
          const key = col.dataField;
          const label = col.caption;
          let value: string | React.ReactNode = col?.render
            ? col.render({ rowData: defaultData })
            : (defaultData[key as keyof File] as string);

          // prevents icon label from showing, we just want the file name here
          if (key === "name") {
            value = defaultData.name;
          }

          return (
            <DetailItem key={key}>
              <DetailLabel>{label}</DetailLabel>
              <DetailValue>{value}</DetailValue>
            </DetailItem>
          );
        })}
        {evidenceFile && evidenceItem && (
          <DetailItem data-testid="case-file-evidence-item-row">
            <DetailLabel>Evidence Item</DetailLabel>
            <DetailValue>
              <EvidenceItemLink
                to={evidenceItemFilesPath(
                  evidenceItem.uuid,
                  defaultData.case?.case_id ?? evidenceItem.case_id
                )}
              >
                {evidenceItem.evidence_number}
              </EvidenceItemLink>
            </DetailValue>
          </DetailItem>
        )}
        {!caseless && (
          <div style={{ margin: "10px 0px" }}>
            <DetailItem style={{ marginBottom: 5 }}>
              <DetailLabel>Shared With</DetailLabel>
            </DetailItem>
            <SharedWithComponent item={defaultData} />
          </div>
        )}
        <DetailItem>
          <DetailLabel>Description</DetailLabel>
        </DetailItem>
        <TextAreaInput
          minRows={6}
          maxRows={12}
          defaultValue={defaultData.description || "None"}
          // The Files API refuses a description on the managed folders.
          readOnly={readOnly}
          onChange={(e) => {
            if (!readOnly) handleDescriptionChange(e.target.value);
          }}
          style={{ backgroundColor: "transparent" }}
        />
      </div>
    </StyledContainer>
  );
};
